import React, { useEffect, useState } from 'react';
import { Grid, Typography, IconButton, Menu, ListItemIcon } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { displaySentence } from './Folder';
import CustomModal from '../Common/CustomModal';
import DeleteActivityModal from './DeleteActivity';
import MoveActivityModal from './MoveActivity';
import Item from '../Common/Item';
import {
  ActivityLibraries,
  IFolderType,
  closeDeleteActivityModalOnSuccess,
  closeEditActivityModalOnSuccess,
  deleteActivity,
  permenantlyDeleteActivity,
  resetPermenantlyDeleteActivitySuccess,
  restoreActivity,
} from '../../redux/activityLibrary/activityLibrarySlice';
import { useAppSelector } from '../../redux/hooks';
import StyledMenuItem from '../Common/StyledMenuItem';
import { transformEditActivityData } from '../../utils/utils';
import { setDraftActivity } from '../../redux/activity/activitySlice';
import EditActivityModal from './CustomizeActivityModal';
import { ACTIVITY_LIBRARY_ROUTE } from '../../utils/routes';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

interface IActivityProps {
  activity: ActivityLibraries;
  isSelected: boolean;
  onClick: () => void;
}

function Activity({ activity, isSelected, onClick = () => {} }: IActivityProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const {
    isDeleteActivitySuccess,
    deleteActivityLoading,
    permenantlyDeleteActivitySuccess,
    isAEditActivitySuccess,
  } = useAppSelector((state) => state.activityLibrary);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const { folderType } = useAppSelector((state) => state.activityLibrary.folder);
  const folderId = useAppSelector((state) => state.activityLibrary.folder.id);
  const folders = useAppSelector((state) => state.activityLibrary.userFolders);

  const handleCloseModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDelete = () => {
    if (!coachId) return;
    dispatch(
      deleteActivity({
        id: activity.id,
        coachId,
        isMoveToTrash: true,
        isInsideFolder: folderId !== '',
      })
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRestore = () => {
    if (!coachId) return;
    dispatch(restoreActivity({ id: activity.id, coachId }));
  };

  const handlePermenatlyDelete = () => {
    dispatch(permenantlyDeleteActivity({ id: activity.id }));
  };

  useEffect(() => {
    if (isDeleteActivitySuccess) {
      handleCloseModal();
      dispatch(closeDeleteActivityModalOnSuccess());
    }
  }, [isDeleteActivitySuccess]);

  useEffect(() => {
    if (permenantlyDeleteActivitySuccess) {
      handleCloseModal();
      dispatch(resetPermenantlyDeleteActivitySuccess());
    }
  }, [permenantlyDeleteActivitySuccess]);

  const handleEditClick = () => {
    setAnchorEl(null);
    setIsEditModalOpen(true);
    const transformedActivity = transformEditActivityData(activity);
    dispatch(setDraftActivity(transformedActivity));
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  useEffect(() => {
    if (isAEditActivitySuccess) {
      handleCloseEditModal();
      dispatch(closeEditActivityModalOnSuccess());
    }
  }, [isAEditActivitySuccess]);

  return (
    <Grid item xs={4}>
      {location.pathname === ACTIVITY_LIBRARY_ROUTE ? (
        <Item>
          <Grid display="flex" alignItems="center" gap="10px">
            <img src="/assets/icons/document.svg" alt="document" width={32} height={32} />
            <Typography variant="bodyBold" color="primary">
              {displaySentence(activity.name)}
            </Typography>
          </Grid>
          {checkPermissions(
            [
              UserPermissionType.ACTIVITY_LIBRARY_MODULE_DELETE_ACTIVITY_LIBRARY,
              UserPermissionType.ACTIVITY_LIBRARY_MODULE_UPDATE_ACTIVITY_LIBRARY,
            ],
            false
          ) && (
            <IconButton onClick={handleClick}>
              <MoreVertIcon style={{ color: '#7CAEBE' }} />
            </IconButton>
          )}
        </Item>
      ) : (
        <Item
          sx={{
            cursor: 'pointer',
            border: isSelected ? '2px solid var(--Accent-Deep-Koamaru, #2A3C67)' : 'none',
          }}
          onClick={onClick}
        >
          <Grid display="flex" alignItems="center" gap="10px">
            <img src="/assets/icons/document.svg" alt="document" width={32} height={32} />
            <Typography variant="bodyBold" color="primary">
              {displaySentence(activity.name)}
            </Typography>
          </Grid>
          {isSelected && (
            <img
              src="/assets/icons/selected-activity.svg"
              alt="selected-activity"
              width={32}
              height={32}
            />
          )}
        </Item>
      )}

      {folderType === IFolderType.TRASH ? (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <StyledMenuItem onClick={handleRestore}>
            <ListItemIcon>
              <img src="/assets/icons/edit-pen.svg" alt="edit-pen" />
            </ListItemIcon>
            <Typography variant="bodyRegular" color="primary">
              {t('activityLibrary.trash.restore')}
            </Typography>
          </StyledMenuItem>

          <StyledMenuItem
            onClick={() => {
              handleClose();
              setIsDeleteModalOpen(true);
            }}
          >
            <ListItemIcon>
              <img src="/assets/icons/trash-bin-red.svg" alt="edit-pen" />
            </ListItemIcon>
            <Typography variant="bodyRegular" color="primary">
              {t('activityLibrary.filled.delete')}
            </Typography>
          </StyledMenuItem>
        </Menu>
      ) : (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {checkPermissions([
            UserPermissionType.ACTIVITY_LIBRARY_MODULE_UPDATE_ACTIVITY_LIBRARY,
          ]) && (
            <StyledMenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <img src="/assets/icons/edit-pen.svg" alt="edit-pen" />
              </ListItemIcon>
              <Typography variant="bodyRegular" color="primary">
                {t('activityLibrary.filled.edit')}
              </Typography>
            </StyledMenuItem>
          )}

          {checkPermissions([
            UserPermissionType.ACTIVITY_LIBRARY_MODULE_UPDATE_ACTIVITY_LIBRARY,
          ]) && (
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setIsMoveModalOpen(true);
              }}
              disabled={!folderId && folders.length === 0}
            >
              <ListItemIcon>
                <img src="/assets/icons/copy.svg" alt="copy" />
              </ListItemIcon>
              <Typography variant="bodyRegular" color="primary">
                {t('activityLibrary.filled.move')}
              </Typography>
            </StyledMenuItem>
          )}

          {checkPermissions([
            UserPermissionType.ACTIVITY_LIBRARY_MODULE_DELETE_ACTIVITY_LIBRARY,
          ]) && (
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setIsDeleteModalOpen(true);
              }}
            >
              <ListItemIcon>
                <img src="/assets/icons/trash-bin-red.svg" alt="edit-pen" />
              </ListItemIcon>
              <Typography variant="bodyRegular" color="primary">
                {t('activityLibrary.filled.delete')}
              </Typography>
            </StyledMenuItem>
          )}
        </Menu>
      )}
      {isDeleteModalOpen && folderType !== IFolderType.TRASH && (
        <CustomModal isOpen={isDeleteModalOpen} xs={3.3}>
          <DeleteActivityModal
            handleDelete={handleDelete}
            handleDismiss={handleCloseModal}
            activityName={activity.name}
            loading={deleteActivityLoading}
            loadingText={t('activityLibrary.filled.deletingActivity')}
          />
        </CustomModal>
      )}
      {isEditModalOpen && (
        <CustomModal isOpen={isEditModalOpen} xs={11}>
          <EditActivityModal
            closeModel={() => setIsEditModalOpen(false)}
            isEditMode
            activityId={activity.id}
          />
        </CustomModal>
      )}
      {isMoveModalOpen && (
        <CustomModal isOpen={isMoveModalOpen} xs={3.3}>
          <MoveActivityModal
            handleDismiss={() => setIsMoveModalOpen(false)}
            activityId={activity.id}
          />
        </CustomModal>
      )}
      {isDeleteModalOpen && folderType === IFolderType.TRASH && (
        <CustomModal isOpen={isDeleteModalOpen} xs={3.3}>
          <DeleteActivityModal
            handleDelete={handlePermenatlyDelete}
            handleDismiss={handleCloseModal}
            activityName={activity.name}
            isPermenantlyDelete
            loading={deleteActivityLoading}
            loadingText={t('activityLibrary.filled.deletingActivity')}
          />
        </CustomModal>
      )}
    </Grid>
  );
}

export default Activity;
